import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_NAME = "ViraLens";
const DEFAULT_OG_IMAGE = "/opengraph.jpg";

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  ogImage?: string;
  noIndex?: boolean;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function buildPath(path: string, lang: string): string {
  const cleaned = path.startsWith("/") ? path : `/${path}`;
  if (lang === "es") {
    if (cleaned === "/") return "/es/";
    return `/es${cleaned}`;
  }
  return cleaned;
}

export function SEO({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  type = "website",
  jsonLd,
}: SEOProps) {
  const { t, i18n } = useTranslation("landing");
  const defaultTitle = `ViraLens | ${t("seo.defaultTitle")}`;
  const defaultDescription = t("seo.defaultDescription");
  const desc = description || defaultDescription;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : defaultTitle;
  const PROD_ORIGIN = (import.meta.env.VITE_SITE_ORIGIN as string | undefined) || "https://app.viralens.ai";
  const canonicalBase =
    typeof window !== "undefined" && window.location.origin && !window.location.origin.includes("localhost") && !window.location.origin.includes("replit")
      ? window.location.origin
      : PROD_ORIGIN;

  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";
  const enPath = buildPath(path, "en");
  const esPath = buildPath(path, "es");
  const enUrl = `${canonicalBase}${enPath === "/" ? "" : enPath}`;
  const esUrl = `${canonicalBase}${esPath}`;
  const canonical = currentLang === "es" ? esUrl : enUrl;
  const ogLocale = currentLang === "es" ? "es_ES" : "en_US";

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />

      {!noIndex && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {!noIndex && <link rel="alternate" hrefLang="es" href={esUrl} />}
      {!noIndex && <link rel="alternate" hrefLang="x-default" href={enUrl} />}

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
